import { useState, useEffect } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiHome, 
  FiBox, 
  FiShoppingBag, 
  FiUsers, 
  FiSettings, 
  FiPieChart,
  FiTag,
  FiMessageSquare,
  FiX,
  FiHelpCircle,
  FiPlus,
  FiEdit,
  FiList,
  FiLogOut,
  FiGift,
  FiGrid,
  FiBookmark,
  FiPackage,
  FiLayers,
  FiImage,
  FiUpload,
  FiFolder,
  FiFolderPlus,
  FiRefreshCcw
} from 'react-icons/fi';
import api from '../../api/api';

const Sidebar = ({ onClose, onLogout }) => {
  const [openMenus, setOpenMenus] = useState({});
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [logo, setLogo] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchLogo = async () => {
      try {
        const response = await api.getLogo();
        if (response?.image) {
          setLogo(response.image);
        }
      } catch (error) {
        console.error('Error fetching logo:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchLogo();
  }, []);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
  };

  const handleConfirmLogout = () => {
    setShowLogoutConfirm(false);
    onLogout();
  };

  const toggleSubmenu = (menuName) => {
    setOpenMenus(prev => ({
      ...prev,
      [menuName]: !prev[menuName]
    }));
  };

  const menuItems = [
    { icon: FiHome, name: 'Dashboard', path: '/admin/dashboard' },
    {
      icon: FiBox,
      name: 'Products',
      submenu: [
        { icon: FiList, name: 'Products List', path: '/admin/products/list' },
        { icon: FiPlus, name: 'Add Product', path: '/admin/products/add' }  // This should now point to the wizard
      ]
    },
    { icon: FiShoppingBag, name: 'Orders', path: '/admin/orders' },
    { icon: FiUsers, name: 'Customers', path: '/admin/customers' },
    { 
      icon: FiRefreshCcw, 
      name: 'Refund Requests', 
      path: '/admin/refund-requests'  // Ensure this matches your route exactly
    },
    {
      icon: FiGift,
      name: 'Offers',
      submenu: [
        { icon: FiList, name: 'All Offers', path: '/admin/offers/list' },
        { icon: FiPlus, name: 'Create Offer', path: '/admin/offers/create' }
      ]
    },
    { icon: FiImage, name: 'Media', path: '/admin/media' },
    {
      icon: FiTag,
      name: 'Taxonomy',
      path: '/admin/taxonomy'
    },
    { icon: FiPieChart, name: 'Analytics', path: '/admin/analytics' },
    { icon: FiMessageSquare, name: 'Reviews', path: '/admin/reviews' },
    { icon: FiSettings, name: 'Settings', path: '/admin/settings' }
  ];

  return (
    <>
      <aside className="bg-white w-64 h-screen flex flex-col shadow-xl">
        <div className="p-6 border-b border-gray-200">
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex flex-col items-center"
          >
            {/* Logo Section */}
            <div className="text-center">
              <Link to="/admin/dashboard">
                {isLoading ? (
                  <div className="h-20 w-20 mx-auto animate-pulse bg-gray-200 rounded"></div>
                ) : logo ? (
                  <img 
                    src={logo}
                    alt="Site Logo"
                    className="h-20 w-auto mx-auto transition-transform duration-300 hover:scale-105 object-contain"
                  />
                ) : (
                  <div className="h-20 w-20 mx-auto flex items-center justify-center bg-gray-100 rounded">
                    <span className="text-gray-400">No Logo</span>
                  </div>
                )}
              </Link>
            </div>
          </motion.div>
          <button
            onClick={onClose}
            className="lg:hidden absolute top-4 right-4 text-gray-500 hover:text-gray-700"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        <nav className="flex-1 overflow-y-auto py-6">
          <div className="px-4 space-y-1">
            {menuItems.map((item, index) => (
              <motion.div
                key={item.path || item.name}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                {item.submenu ? (
                  <div>
                    <button
                      onClick={() => toggleSubmenu(item.name)}
                      className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 text-gray-600 hover:bg-gray-50`}
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="font-medium">{item.name}</span>
                      <svg
                        className={`ml-auto h-5 w-5 transform transition-transform ${
                          openMenus[item.name] ? 'rotate-180' : ''
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                    <AnimatePresence>
                      {openMenus[item.name] && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: 'auto' }}
                          exit={{ opacity: 0, height: 0 }}
                          className="ml-4 mt-2 space-y-1"
                        >
                          {item.submenu.map((subItem) => (
                            <NavLink
                              key={subItem.path}
                              to={subItem.path}
                              className={({ isActive }) =>
                                `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                                  isActive
                                    ? 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-600 shadow-sm'
                                    : 'text-gray-600 hover:bg-gray-50'
                                }`
                              }
                              end  // Add this prop to ensure exact path matching
                            >
                              <subItem.icon className="h-5 w-5" />
                              <span className="font-medium">{subItem.name}</span>
                            </NavLink>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ) : (
                  !item.submenu && (
                    <NavLink
                      to={item.path}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-4 py-3 rounded-lg transition-all duration-200 ${
                          isActive
                            ? 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-600 shadow-sm'
                            : 'text-gray-600 hover:bg-gray-50'
                        }`
                      }
                    >
                      <item.icon className="h-5 w-5" />
                      <span className="font-medium">{item.name}</span>
                    </NavLink>
                  )
                )}
              </motion.div>
            ))}
          </div>
        </nav>

        {/* Bottom Help Section */}
        <div className="p-4 border-t border-gray-200">
          <NavLink
            to="/admin/help-support"
            className={({ isActive }) =>
              `flex items-center gap-3 px-4 py-3 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors ${
                isActive ? 'bg-gradient-to-r from-indigo-50 to-purple-50 text-indigo-600 shadow-sm' : ''
              }`
            }
          >
            <FiHelpCircle className="h-5 w-5" />
            <span className="font-medium">Help & Support</span>
          </NavLink>
        </div>

        {/* New Logout Section */}
        <div className="mt-auto border-t border-gray-200">
          <div className="p-4">
            <button
              onClick={handleLogoutClick}
              className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-red-600 hover:bg-red-50 transition-all duration-200"
            >
              <FiLogOut className="h-5 w-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>

      {/* Logout Confirmation Modal */}
      <AnimatePresence>
        {showLogoutConfirm && (
          <>
            {/* Semi-transparent Backdrop (contained within sidebar) */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-black/20 backdrop-blur-sm z-[101]"
              onClick={() => setShowLogoutConfirm(false)}
            />
            
            {/* Modal Container - Centered in Sidebar */}
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ 
                type: "spring",
                stiffness: 300,
                damping: 30
              }}
              className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] z-[102]"
            >
              {/* Modal Content */}
              <div className="relative bg-white rounded-xl overflow-hidden shadow-lg border border-gray-100">
                {/* Decorative Top Bar */}
                <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500" />
                
                {/* Content Container */}
                <div className="pt-6 pb-6 px-4">
                  <div className="flex flex-col items-center text-center">
                    {/* Icon Container */}
                    <div className="mb-3 p-2.5 rounded-full bg-red-50">
                      <FiLogOut className="h-5 w-5 text-red-600" />
                    </div>
                    
                    {/* Title with Gradient */}
                    <h3 className="text-lg font-semibold bg-gradient-to-r from-gray-900 to-gray-700 bg-clip-text text-transparent mb-1.5">
                      Confirm Logout
                    </h3>
                    
                    {/* Description */}
                    <p className="text-sm text-gray-600 mb-4">
                      Are you sure you want to logout?
                    </p>
                    
                    {/* Buttons Container */}
                    <div className="flex gap-2 w-full">
                      {/* Cancel Button */}
                      <button
                        onClick={() => setShowLogoutConfirm(false)}
                        className="flex-1 px-3 py-2 rounded-lg border border-gray-200 text-gray-700 text-sm font-medium hover:bg-gray-50 hover:border-gray-300 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-gray-200 focus:ring-offset-2"
                      >
                        Cancel
                      </button>
                      
                      {/* Logout Button */}
                      <button
                        onClick={handleConfirmLogout}
                        className="flex-1 px-3 py-2 rounded-lg bg-gradient-to-r from-red-600 to-red-500 text-white text-sm font-medium hover:from-red-700 hover:to-red-600 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Decorative Bottom Pattern */}
                <div className="absolute bottom-0 inset-x-0 h-8 bg-gradient-to-b from-transparent to-gray-50/50" />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Sidebar;






